import { Button, Wordmark } from '@mcgames/ui';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useSession } from '../api/auth-store.ts';
import { useLogout } from '../api/hooks.ts';
import { useIndexerStatus } from '../pages/indexer/indexer-hooks.ts';
import styles from './AdminLayout.module.css';
import { LanguageSwitch } from './LanguageSwitch.tsx';

const NAV_GROUPS = [
  {
    key: 'catalog',
    items: [
      { to: '/admin/games', key: 'games' },
      { to: '/admin/store', key: 'store' },
      { to: '/admin/explore', key: 'explore' },
    ],
  },
  {
    key: 'sourcing',
    items: [
      { to: '/admin/indexer', key: 'indexer' },
      { to: '/admin/imports', key: 'imports' },
    ],
  },
  {
    key: 'access',
    items: [
      { to: '/admin/users', key: 'users' },
      { to: '/admin/activity', key: 'activity' },
      { to: '/admin/api-keys', key: 'apiKeys' },
    ],
  },
  {
    key: 'client',
    items: [{ to: '/admin/releases', key: 'releases' }],
  },
] as const;

const linkClass = ({ isActive }: { isActive: boolean }) =>
  [styles['link'], isActive ? styles['active'] : ''].filter(Boolean).join(' ');

/** Side navigation (a top bar with a menu button on phones) around the admin pages. */
export function AdminLayout() {
  const { t } = useTranslation('admin');
  const session = useSession();
  const logout = useLogout();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const panelId = useId();
  const groupId = useId();
  // The indexer page is only offered once the server has an indexer configured.
  const indexerEnabled = useIndexerStatus().data?.enabled === true;
  const navGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.key !== 'indexer' || indexerEnabled),
  }));

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className={styles['shell']}>
      <aside className={styles['sidebar']}>
        <div className={styles['brand']}>
          <Wordmark />
          <Button
            variant="ghost"
            size="sm"
            className={styles['menuButton']}
            aria-expanded={menuOpen}
            aria-controls={panelId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {t('nav.menu')}
          </Button>
        </div>
        <div
          id={panelId}
          className={[styles['panel'], menuOpen ? styles['open'] : ''].filter(Boolean).join(' ')}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setMenuOpen(false);
          }}
        >
          <nav aria-label={t('nav.label')} className={styles['nav']}>
            {navGroups.map((group) => (
              <div key={group.key} className={styles['group']}>
                <span id={`${groupId}-${group.key}`} className={styles['groupLabel']}>
                  {t(`nav.groups.${group.key}`)}
                </span>
                <ul className={styles['list']} aria-labelledby={`${groupId}-${group.key}`}>
                  {group.items.map((item) => (
                    <li key={item.key}>
                      <NavLink to={item.to} className={linkClass}>
                        {t(`nav.${item.key}`)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <div className={styles['footer']}>
            {session.user !== null && (
              <span className={styles['user']}>
                {t('header.signedInAs', { username: session.user.username })}
              </span>
            )}
            <NavLink to="/account" className={linkClass}>
              {t('header.account')}
            </NavLink>
            <div className={styles['actions']}>
              <LanguageSwitch />
              <Button variant="secondary" size="sm" onClick={() => logout.mutate()}>
                {t('header.signOut')}
              </Button>
            </div>
          </div>
        </div>
      </aside>
      <main className={styles['content']}>
        <Outlet />
      </main>
    </div>
  );
}
